// 定数定義
const BREAKPOINT_SP = 900; // SP/PCの切り替えブレークポイント

const MOSAIC_LAYOUTS = {
  PC: { rows: 11, cols: 28 },
  SP: { rows: 14, cols: 23 }
};

const MOSAIC_DEFAULTS = {
  rows: MOSAIC_LAYOUTS.PC.rows,
  cols: MOSAIC_LAYOUTS.PC.cols,
  duration: 700,           // ms
  stagger: 42,             // ms
  pattern: 'random',       // 'random' | 'center' | 'diagonal' | 'rows' | 'cols' | 'spiral'
  maxRandomDelay: 480,     // ms
  blur: 0,                 // px
  scale: 1,
  gap: 0,                  // px
  overlap: 0,              // px
  once: true,              // 一度だけ再生（true推奨）
  cacheBust: false         // キャッシュバスティング（スーパーリロード問題対策）
};

// 遅延パターン
const delayPatterns = {
  random: ({r, c, rows, cols, opt}) => Math.random() * opt.maxRandomDelay,
  center: ({r, c, rows, cols, opt}) => {
    const cr = (rows - 1) / 2, cc = (cols - 1) / 2;
    const d = Math.hypot(r - cr, c - cc);
    return d * opt.stagger + Math.random() * (opt.stagger * .6);
  },
  diagonal: ({r, c, rows, cols, opt}) => (r + c) * opt.stagger,
  rows: ({r, c, rows, cols, opt}) => r * opt.stagger,
  cols: ({r, c, rows, cols, opt}) => c * opt.stagger,
  spiral: ({r, c, rows, cols, opt}) => {
    const cr = (rows - 1) / 2, cc = (cols - 1) / 2;
    const dy = r - cr, dx = c - cc;
    const angle = (Math.atan2(dy, dx) + Math.PI) / (2 * Math.PI); // 0..1
    const dist = Math.hypot(dx, dy);
    return (dist * opt.stagger) + angle * opt.maxRandomDelay * .6;
  }
};

function buildMosaic(el, src, options = {}) {
  const opt = {...MOSAIC_DEFAULTS, ...options};
  el.style.setProperty('--rows', opt.rows);
  el.style.setProperty('--cols', opt.cols);
  el.style.setProperty('--tile-gap', opt.gap + 'px');
  el.style.setProperty('--tile-overlap', opt.overlap + 'px');
  el.style.setProperty('--blur', opt.blur + 'px');
  el.style.setProperty('--scale', opt.scale);
  el.style.setProperty('--dur', opt.duration + 'ms');
  el.style.setProperty('--stagger', opt.stagger + 'ms');

  // 画像読み込み→自然サイズから aspect-ratio を設定
  const img = new Image();
  img.decoding = 'async';
  // img.crossOrigin = 'anonymous'; // 一時的に無効化（CORS問題の可能性）

  // キャッシュバスティング（スーパーリロード問題対策）
  let imageSrc = src;
  if (opt.cacheBust) {
    const separator = src.includes('?') ? '&' : '?';
    imageSrc = `${src}${separator}_cb=${Date.now()}`;
  }

  // 画像読み込み完了を待つ
  const loadImage = () => {
    return new Promise((resolve, reject) => {
      img.onload = () => {
        el.style.setProperty('--img-w', img.naturalWidth);
        el.style.setProperty('--img-h', img.naturalHeight);
        resolve();
      };
      img.onerror = reject;

      img.src = imageSrc;
    });
  };

  // 非同期でタイルを作成
  const createTiles = async () => {
    try {
      await loadImage();

      // grid 差し替え
      el.innerHTML = '';
      const grid = document.createElement('div');
      grid.className = 'mosaic__grid';

      // 画面幅でレイアウトを決定（optionsで渡されたrows/colsを使用）
      const isSp = opt.rows === MOSAIC_LAYOUTS.SP.rows && opt.cols === MOSAIC_LAYOUTS.SP.cols;

      // 列幅と累積位置を事前計算
      const { columnWidths, columnPositions, rowHeights, rowPositions, totalWidth, totalHeight } =
        calculateGridLayout(opt.rows, opt.cols, isSp);

      grid.style.gridTemplateColumns = columnWidths.join(' ');
      grid.style.gridTemplateRows = rowHeights.join(' ');
      el.appendChild(grid);

      // 背景スタイル値の文字列を事前計算
      const bgSizeValue = `${totalWidth}px ${totalHeight}px`;
      const makeDelay = delayPatterns[opt.pattern] || delayPatterns.random;

      // タイルを作成
      for (let r = 0; r < opt.rows; r++) {
        for (let c = 0; c < opt.cols; c++) {
          const tile = document.createElement('div');
          tile.className = 'mosaic__tile';

          // 累積幅と高さ（事前計算済み）
          const bgPosX = `-${columnPositions[c]}px`;
          const bgPosY = `-${rowPositions[r]}px`;

          // スタイルを一括設定
          const baseStyle = `
            background-image: url('${imageSrc}');
            background-size: ${bgSizeValue};
            background-position: ${bgPosX} ${bgPosY};
            background-repeat: no-repeat;
          `;

          // アニメーションをスキップする場合は即座に表示
          if (opt.skipAnimation) {
            tile.style.cssText = baseStyle + 'opacity: 1;';
          } else {
            tile.style.cssText = baseStyle + `--delay: ${makeDelay({r, c, rows: opt.rows, cols: opt.cols, opt})}ms;`;
          }

          grid.appendChild(tile);
        }
      }

      // タイル作成完了後にアニメーション開始（スキップ時は追加しない）
      if (!opt.skipAnimation) {
        el.classList.add('is-animating');
      } else {
        // リサイズ時の再構築完了を検知できるように、opacityを設定
        requestAnimationFrame(() => {
          el.style.opacity = '1';
        });
      }

      // 現在使用中の画像ソースを保存
      el.dataset.currentSrc = imageSrc;

      // 現在の画面幅状態（SPかどうか）を保存
      el.dataset.wasSp = isSpBreakpoint().toString();

    } catch (error) {
      console.error('画像の読み込みに失敗しました:', error);
      // エラー時はアニメーションなしで表示
      if (!opt.skipAnimation) {
        el.classList.add('is-animating');
      }
      // エラー時も画面幅状態を保存
      el.dataset.wasSp = (window.innerWidth <= 900).toString();
    }
  };

  createTiles();
  return { options: opt };
}

/**
 * SPかどうかを判定
 * @returns {boolean} SPの場合true
 */
function isSpBreakpoint() {
  return window.innerWidth <= BREAKPOINT_SP;
}

/**
 * 画面幅とRetina対応に応じた画像パスを取得
 * @param {HTMLElement} el - mosaic要素
 * @returns {string} 画像パス
 */
function getImageSource(el) {
  const isRetina = window.devicePixelRatio >= 2;
  const isSp = isSpBreakpoint();

  const baseSrc = (isSp && el.dataset.imageSp)
    ? el.dataset.imageSp
    : (el.dataset.image || '');

  // Retina対応：@2x画像がある場合は使用
  if (isRetina && baseSrc) {
    return baseSrc.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '@2x.$1');
  }

  return baseSrc;
}

/**
 * SP/PCに応じた行数・列数を取得
 * @param {HTMLElement} el - mosaic要素
 * @param {Object} options - オプション
 * @returns {{rows: number, cols: number}} 行数と列数
 */
function getMosaicDimensions(el, options = {}) {
  const isSp = isSpBreakpoint();
  const layout = isSp ? MOSAIC_LAYOUTS.SP : MOSAIC_LAYOUTS.PC;

  return {
    rows: parseInt(
      el.dataset.rows || options.rows || layout.rows,
      10
    ),
    cols: parseInt(
      el.dataset.cols || options.cols || layout.cols,
      10
    )
  };
}

/**
 * グリッドレイアウトを計算
 * @param {number} rows - 行数
 * @param {number} cols - 列数
 * @param {boolean} isSp - SPかどうか
 * @returns {Object} レイアウト情報
 */
function calculateGridLayout(rows, cols, isSp) {
  const columnWidths = [];
  const columnPositions = [];
  const rowHeights = [];
  const rowPositions = [];
  let cumulativeWidth = 0;
  let cumulativeHeight = 0;

  if (isSp) {
    // SP用レイアウト：列幅 1列目と23列目が22px、それ以外が36px
    // 行高さ：1行目が18px、それ以外が36px
    for (let c = 0; c < cols; c++) {
      const width = (c === 0 || c === cols - 1) ? 22 : 36;
      columnWidths.push(`${width}px`);
      columnPositions.push(cumulativeWidth);
      cumulativeWidth += width;
    }
    for (let r = 0; r < rows; r++) {
      const height = r === 0 ? 18 : 36;
      rowHeights.push(`${height}px`);
      rowPositions.push(cumulativeHeight);
      cumulativeHeight += height;
    }
  } else {
    // PC用レイアウト：列幅 1列目と28列目が41px、それ以外が50px
    // 行高さ：すべて50px
    for (let c = 0; c < cols; c++) {
      const width = (c === 0 || c === cols - 1) ? 41 : 50;
      columnWidths.push(`${width}px`);
      columnPositions.push(cumulativeWidth);
      cumulativeWidth += width;
    }
    for (let r = 0; r < rows; r++) {
      rowHeights.push('50px');
      rowPositions.push(cumulativeHeight);
      cumulativeHeight += 50;
    }
  }

  return {
    columnWidths,
    columnPositions,
    rowHeights,
    rowPositions,
    totalWidth: cumulativeWidth,
    totalHeight: cumulativeHeight
  };
}

/**
 * 要素が可視領域にあるかチェック（簡易版）
 */
function isElementInViewport(el) {
  const rect = el.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= windowHeight &&
    rect.right <= windowWidth
  ) || (
    rect.bottom > 0 &&
    rect.top < windowHeight &&
    rect.right > 0 &&
    rect.left < windowWidth &&
    rect.height > 0 &&
    rect.width > 0
  );
}

/**
 * 可視領域に入ったときに自動アニメーション
 */
function autoAnimateOnView(targets, options = {}) {
  // アニメーション開始処理を共通化
  const startAnimation = (el) => {
    const src = getImageSource(el);
    const { rows, cols } = getMosaicDimensions(el, options);
    const pattern = el.dataset.pattern || options.pattern || MOSAIC_DEFAULTS.pattern;
    buildMosaic(el, src, { ...options, rows, cols, pattern });
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const el = entry.target;
      if (entry.isIntersecting) {
        // まだモザイクが構築されていない場合のみ実行
        if (el.innerHTML.trim() === '' || !el.querySelector('.mosaic__grid')) {
          startAnimation(el);
        }

        if ((options.once ?? MOSAIC_DEFAULTS.once) === true) {
          io.unobserve(el);
        }
      }
    });
  }, {
    threshold: .25
  });

  targets.forEach(el => {
    // 初期状態で既に可視領域にある場合は即座にアニメーション開始
    // これはスマホ実機でページロード時に要素が既に表示されている場合に対応
    if (isElementInViewport(el)) {
      // requestAnimationFrameを使用して、DOMの準備が整ってから実行
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // まだモザイクが構築されていない場合のみ実行
          if (el.innerHTML.trim() === '' || !el.querySelector('.mosaic__grid')) {
            startAnimation(el);
            // 一度実行したら監視を解除
            if ((options.once ?? MOSAIC_DEFAULTS.once) === true) {
              io.unobserve(el);
            }
          }
        });
      });
    } else {
      // 可視領域にない場合はIntersectionObserverで監視
      io.observe(el);
    }
  });
}

/**
 * モザイクを切り替える（リサイズ時用）
 * @param {HTMLElement} el - mosaic要素
 * @param {string} newSrc - 新しい画像URL
 */
function switchMosaic(el, newSrc) {
  const isSp = isSpBreakpoint();
  const { rows, cols } = getMosaicDimensions(el);
  const pattern = el.dataset.pattern || MOSAIC_DEFAULTS.pattern;

  // 古い画像を即座に非表示
  el.classList.remove('is-animating');
  el.style.opacity = '0';
  el.style.transition = 'opacity 0s';

  // 新しい画像を先に読み込んでから切り替える
  const preloadImg = new Image();
  preloadImg.onload = () => {
    el.innerHTML = '';
    buildMosaic(el, newSrc, { rows, cols, pattern, skipAnimation: true });
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transition = '';
    });
  };
  preloadImg.onerror = () => {
    el.style.opacity = '1';
    el.style.transition = '';
    console.error('画像の読み込みに失敗しました:', newSrc);
  };
  preloadImg.src = newSrc;
}

/**
 * リサイズ時に画像を切り替える処理
 * BREAKPOINT_SPの境界をまたいだ場合のみ実行（即座に実行）
 */
function handleResize() {
  const mosaics = Array.from(document.querySelectorAll('.mosaic'));
  mosaics.forEach(el => {
    // 既にモザイクが構築されている場合のみチェック
    if (el.classList.contains('is-animating') || el.innerHTML.trim() !== '') {
      const wasSp = el.dataset.wasSp === 'true';
      const isSpNow = isSpBreakpoint();

      // 境界をまたいだ場合のみ処理を実行
      if (wasSp !== isSpNow) {
        const newSrc = getImageSource(el);
        switchMosaic(el, newSrc);
      }
    }
  });
}

/**
 * リサイズイベントの処理（境界をまたいだ時のみ即座に実行、それ以外はデバウンス）
 */
let resizeTimer = null;
let lastWidth = window.innerWidth;

function onResize() {
  const currentWidth = window.innerWidth;
  const wasSp = lastWidth <= BREAKPOINT_SP;
  const isSpNow = currentWidth <= BREAKPOINT_SP;

  // 境界をまたいだ場合は即座に実行
  if (wasSp !== isSpNow) {
    if (resizeTimer) {
      clearTimeout(resizeTimer);
      resizeTimer = null;
    }
    handleResize();
    lastWidth = currentWidth;
  } else {
    // 境界をまたいでない場合はデバウンス処理（パフォーマンス最適化）
    if (resizeTimer) {
      clearTimeout(resizeTimer);
    }
    resizeTimer = setTimeout(() => {
      lastWidth = window.innerWidth;
      resizeTimer = null;
    }, 250);
  }
}

// 起動：ページロード後に監視開始
window.addEventListener('DOMContentLoaded', () => {
  const mosaics = Array.from(document.querySelectorAll('.mosaic'));
  autoAnimateOnView(mosaics, {});

  // 初期画面幅を保存
  lastWidth = window.innerWidth;

  // リサイズ時に画像を切り替える（境界をまたいだ時は即座に実行）
  window.addEventListener('resize', onResize);
});

/**
 * Stickyfill の初期化
 */
function initStickyfill() {
  if (typeof Stickyfill !== 'undefined' && typeof $ !== 'undefined') {
    const elements = $('.fixed');
    if (elements.length > 0) {
      Stickyfill.add(elements);
    }
  }
}

// ウィンドウの load と resize で実行
window.addEventListener('load', initStickyfill);
window.addEventListener('resize', initStickyfill);